import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { KeyRound, Pencil } from 'lucide-react';
import { nanoid } from 'nanoid';

/**
 * Custom TipTap node representing a password field.
 *
 * IMPORTANT security decisions:
 *   1. The node is `atom: true` with NO text content — the password value lives
 *      only in the `value` node attribute. It is never inserted into the
 *      editor's visible DOM as text, so it cannot be read by looking at the
 *      screen, selected with the mouse, or found with the browser's Ctrl+F.
 *   2. The React NodeView only renders `🔑 label  [Reveal/Edit]` — never the
 *      value. Revealing the plaintext requires re-entering the Master Code and
 *      always happens in a separate modal, with auto-hide on close.
 *   3. In-editor HTML attribute `value` is still present on the DOM node (TipTap
 *      requires it for the attribute round-trip). This is acceptable because an
 *      attacker with access to the unlocked page's DOM already has the vault
 *      key in JS memory; the value being in an attribute vs text doesn't add
 *      risk but does prevent casual visual disclosure, which is the dominant
 *      real-world threat ("look over someone's shoulder").
 *   4. On Markdown export (by the export-vault feature) the value is written
 *      inside the tag as expected: <msp-password id="..." label="...">VALUE</msp-password>.
 *
 * IDENTITY:
 *   Each field carries a stable `id` attribute (minted via nanoid on insert).
 *   The id disambiguates duplicate-label fields in the audit log and makes
 *   edit/reveal robust against live-collab position drift — we find the node
 *   by id, not by ProseMirror position.
 */
export const PASSWORD_PLACEHOLDER = '••••••••';

// Event dispatched by NodeView buttons; handled by EditorPane.
export interface PasswordFieldAction {
  kind: 'reveal' | 'edit';
  /** Stable id of the target field. Minted on insert. */
  id: string;
  label: string;
  value: string;
}

/** Short, URL-safe id. 10 chars is plenty within one document's scope. */
export function mintPasswordId(): string {
  return nanoid(10);
}

export const PasswordField = Node.create({
  name: 'passwordField',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      id: {
        default: '',
        parseHTML: (el: HTMLElement) => el.getAttribute('id') ?? '',
        renderHTML: (attrs: Record<string, any>) => (attrs.id ? { id: attrs.id } : {}),
      },
      label: {
        default: '',
        parseHTML: (el: HTMLElement) => el.getAttribute('label') ?? '',
        renderHTML: (attrs: Record<string, any>) => ({ label: attrs.label }),
      },
      value: {
        default: '',
        parseHTML: (el: HTMLElement) => {
          // When loading from Markdown-sourced HTML, the value may either live
          // as a "value" attribute (editor-internal representation) OR as the
          // element's text content (Markdown-friendly representation). Accept
          // whichever exists.
          const fromAttr = el.getAttribute('value');
          if (fromAttr != null && fromAttr !== '') return fromAttr;
          return el.textContent ?? '';
        },
        renderHTML: (attrs: Record<string, any>) => ({ value: attrs.value }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'msp-password' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'msp-password',
      mergeAttributes(HTMLAttributes, { class: 'password-field', 'data-password': 'true' }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(PasswordNodeView);
  },

  addCommands() {
    return {
      /**
       * Insert a new password field at the current selection. Mints a new id.
       * Uses an explicit ProseMirror transaction (schema.create + replaceSelectionWith)
       * rather than commands.insertContent({type, attrs}) — the explicit path
       * is more reliably captured by y-prosemirror for atom nodes with custom
       * attributes.
       */
      insertPasswordField:
        (opts: { label: string; value: string; id?: string }) =>
        ({ state, dispatch }: { state: any; dispatch: any }) => {
          const id = opts.id && opts.id.length > 0 ? opts.id : mintPasswordId();
          const node = state.schema.nodes.passwordField.create({
            id,
            label: opts.label,
            value: opts.value,
          });
          if (dispatch) dispatch(state.tr.replaceSelectionWith(node));
          return true;
        },

      /**
       * Update the label/value of an existing password field identified by id.
       * Position-free — walks the doc to find the node. Uses setNodeAttribute
       * per attr so y-prosemirror sees each attribute change individually
       * rather than a node-replacement transaction.
       *
       * Returns false if no field with that id exists (e.g. another user just
       * deleted it). Callers should surface a gentle notice in that case.
       */
      updatePasswordField:
        (opts: { id: string; label: string; value: string }) =>
        ({ state, dispatch }: { state: any; dispatch: any }) => {
          let foundPos = -1;
          state.doc.descendants((node: any, pos: number) => {
            if (foundPos >= 0) return false;
            if (node.type.name === 'passwordField' && node.attrs.id === opts.id) {
              foundPos = pos;
              return false;
            }
            return true;
          });
          if (foundPos < 0) return false;
          if (dispatch) {
            const tr = state.tr;
            const node = state.doc.nodeAt(foundPos);
            // setNodeAttribute is the Yjs-safe, attr-level signal. Fall back
            // to setNodeMarkup if it isn't available in the PM version.
            const setAttr = (tr as any).setNodeAttribute?.bind(tr);
            if (setAttr) {
              setAttr(foundPos, 'label', opts.label);
              setAttr(foundPos, 'value', opts.value);
            } else {
              tr.setNodeMarkup(foundPos, undefined, {
                ...(node ? node.attrs : {}),
                label: opts.label,
                value: opts.value,
              });
            }
            dispatch(tr);
          }
          return true;
        },
    } as any;
  },
});

function PasswordNodeView(props: NodeViewProps) {
  const { node, editor } = props;
  const id = (node.attrs.id as string) || '';
  const label = (node.attrs.label as string) || 'Password';
  const value = (node.attrs.value as string) || '';
  const editable = editor.isEditable;

  function handleClick(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    const detail: PasswordFieldAction = {
      kind: editable ? 'edit' : 'reveal',
      id,
      label,
      value,
    };
    window.dispatchEvent(new CustomEvent('dragonvault:password-field', { detail }));
  }

  return (
    <NodeViewWrapper
      as="span"
      className="password-field"
      contentEditable={false}
      draggable={false}
      onClick={handleClick}
      title={editable ? 'Click to edit this password' : 'Click to reveal password'}
    >
      <KeyRound size={12} className="inline-block align-text-bottom mr-1 text-dragon-500" />
      <span className="align-middle">{label || 'Password'}</span>
      <span className="align-middle ml-2 text-ink-500 select-none font-mono">
        {PASSWORD_PLACEHOLDER}
      </span>
      {editable && <Pencil size={11} className="inline-block align-text-bottom ml-2 text-ink-400" />}
    </NodeViewWrapper>
  );
}
